import { type ElementType } from 'react';

import { type AssetFileKind, type AssetKind, type FileKind } from '@air/api/types';
import {
  Audio,
  Boards,
  CompressedFiles,
  Database,
  Document,
  Folder,
  FolderOpen,
  Image,
  Images,
  Pdf,
  Play,
  Presentations,
  ThreeD,
  Vector,
  Video,
} from '@air/next-icons';

import { type SelectedFilter } from '~/store/filters/types';

export const AllItemTypeFilters = ['boards', 'assets', 'files'] as const;
export type ItemTypeFilter = (typeof AllItemTypeFilters)[number];

export const isItemTypeFilter = (value: string): value is ItemTypeFilter =>
  !!AllItemTypeFilters.find((filter) => filter === value);

export const isAssetTypeFilter = (value: string): value is AssetFileKind =>
  !!Object.values({ ...ASSET_TYPE_FILTERS, ...FILES_TYPE_FILTERS }).find((filter) => filter.id === value);

export type SingleFilterProps = {
  onApplyClick: () => void;
  onRemoveClick?: (filters: SelectedFilter[]) => void;
  onOpenChange?: (open: boolean) => void;
};

export type AssetTypeFilter = {
  id: AssetKind;
  label: string;
  icon: ElementType;
};

export const ASSET_TYPE_FILTERS: Partial<Record<AssetKind, AssetTypeFilter>> = {
  image: {
    id: 'image',
    label: 'Images',
    icon: Image,
  },
  video: {
    id: 'video',
    label: 'Videos',
    icon: Video,
  },
  audio: {
    id: 'audio',
    label: 'Audio & Sounds',
    icon: Audio,
  },
  vector: {
    id: 'vector',
    label: 'Illustrator & Vector Graphics',
    icon: Vector,
  },
  presentation: {
    id: 'presentation',
    label: 'Presentations',
    icon: Presentations,
  },
  pdf: {
    id: 'pdf',
    label: 'PDFs',
    icon: Pdf,
  },
  document: {
    id: 'document',
    label: 'Documents',
    icon: Document,
  },
  rive: {
    id: 'rive',
    label: 'Animations',
    icon: Play,
  },
};

export type FilesTypeFilter = {
  id: FileKind;
  label: string;
  icon: ElementType;
};

export const FILES_TYPE_FILTERS: Record<FileKind, FilesTypeFilter> = {
  compressed: {
    id: 'compressed',
    label: 'Compressed Files',
    icon: CompressedFiles,
  },
  font: {
    id: 'font',
    label: 'Fonts',
    icon: Document,
  },
  spreadsheet: {
    id: 'spreadsheet',
    label: 'Spreadsheets & Databases',
    icon: Database,
  },
  threeD: {
    id: 'threeD',
    label: '3D Objects',
    icon: ThreeD,
  },
  other: {
    id: 'other',
    label: 'All other files',
    icon: FolderOpen,
  },
};

export type TypeFilters = 'boards' | 'assets' | 'files';

export type TypeFilter = {
  id: TypeFilters;
  label: string;
  icon: ElementType;
  subFilters?: Record<FileKind, FilesTypeFilter> | Partial<Record<AssetKind, AssetTypeFilter>>;
};

export const TYPE_FILTERS: Record<TypeFilters, TypeFilter> = {
  boards: {
    id: 'boards',
    label: 'Boards',
    icon: Boards,
  },
  assets: {
    id: 'assets',
    label: 'Assets',
    icon: Images,
    subFilters: ASSET_TYPE_FILTERS,
  },
  files: {
    id: 'files',
    label: 'Files',
    icon: Folder,
    subFilters: FILES_TYPE_FILTERS,
  },
};
